import { UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Form,
  Image,
  Input,
  InputNumber,
  Select,
  Spin,
  Upload,
} from "antd";
import { RcFile, UploadProps } from "antd/es/upload";
import { Package, Plus, X } from "lucide-react";
import { FC, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNotification } from "~/hooks";
import { useGetSubClients } from "~/services/client/client.queries";
import { useCreateMyInventory } from "~/services/inventory";
import { useCreateImages } from "~/services/image";
import {
  useGetPresignedUrls,
  useUploadFileToPresignedUrl,
} from "~/services/upload";
import { TImage } from "~/types";
import { capitalize, getImageUrl } from "~/utils";
import {
  ContainerType,
  InventoryStatusCode,
  InventoryType,
  PackingTypes,
} from "~/utils/constants";
import { useAuth } from "~/store";
import { TPresignedUrl } from "~/services/upload/upload.types";
import { INVENTORY_KEYS } from "~/services/inventory/keys";

const containerTypes = [
  {
    id: ContainerType.CRATE,
    name: "Crate",
    dimensions: { length: 48, width: 48, height: 48 },
  },
  {
    id: ContainerType.SLATCRATE,
    name: "Slat Crate",
    dimensions: { length: 48, width: 48, height: 48 },
  },
  {
    id: ContainerType.T_Frame,
    name: "T-Frame",
    dimensions: { length: 48, width: 8, height: 48 },
  },
  {
    id: ContainerType.PALLET,
    name: "Pallet",
    dimensions: { length: 48, width: 48, height: 48 },
  },
  {
    id: ContainerType.CBIN,
    name: "C-Bin",
    dimensions: { length: 48, width: 24, height: 28 },
  },
  {
    id: ContainerType.SLIPCASE,
    name: "Slipcase",
    dimensions: { length: 48, width: 48, height: 6 },
  },
  {
    id: ContainerType.BOX,
    name: "Box",
    dimensions: { length: 18, width: 18, height: 24 },
  },
  { id: ContainerType.CUSTOM, name: "Custom" },
];

const containerTypeToPackingType = {
  [ContainerType.CRATE]: PackingTypes.CRATE,
  [ContainerType.SLATCRATE]: PackingTypes.SLATCRATE,
  [ContainerType.T_Frame]: PackingTypes.T_Frame,
  [ContainerType.PALLET]: PackingTypes.PALLET,
  [ContainerType.CBIN]: PackingTypes.CBIN,
  [ContainerType.SLIPCASE]: PackingTypes.SLIPCASE,
  [ContainerType.BOX]: PackingTypes.BOX,
  [ContainerType.CUSTOM]: PackingTypes.CUSTOM,
  [ContainerType.PACKAGE]: PackingTypes.BOX,
  [ContainerType.PIECE]: PackingTypes.LOOSE,
};

const mediumTypes = [
  "Oil on Canvas",
  "Acrylic on Canvas",
  "Mixed Media",
  "3D Mixed Media",
  "Sculpture - Bronze",
  "Sculpture - Glass",
  "Photography",
  "Works on Paper",
  "Cast/Forged Metal",
  "2D Flatwork",
  "Other",
];

export interface CreateInventoryFormData {
  itemType: InventoryType;
  clientId: string;
  title: string;
  clientInternalRef?: string;
  length: number;
  width: number;
  height: number;
  weight: number;
  imageIds?: string[];
  notes?: string;
  medium?: string;
  value?: number;
  parts?: number;
  containerType?: string;
  packingType?: string;
  customMedium?: string;
  artistName?: string;
}

interface CreateInventoryFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const CreateInventoryForm: FC<CreateInventoryFormProps> = ({
  onSuccess,
  onCancel,
}) => {
  const { user } = useAuth();
  const client = user?.client;
  const [form] = Form.useForm<CreateInventoryFormData>();
  const itemType = Form.useWatch("itemType", form);
  const containerType = Form.useWatch("containerType", form);
  const medium = Form.useWatch("medium", form);
  const [isLoading, setIsLoading] = useState(false);
  const [previewImages, setPreviewImages] = useState<
    { url: string; file: File }[]
  >([]);
  const [uploading, setUploading] = useState(false);

  // Get subclients
  const { data: subClientsData } = useGetSubClients(client?.id || "", {
    enabled: !!client?.id && !client?.isSubClient,
  });

  // Build client options (current client + subclients)
  const clientOptions = useMemo(() => {
    const options: Array<{ value: string; label: string }> = [];
    
    if (client) {
      const clientName =
        client.companyName ||
        [client.firstName, client.lastName].filter(Boolean).join(" ").trim() ||
        "Unnamed Client";
      options.push({
        value: client.id,
        label: client.isSubClient ? `${clientName} (Subclient)` : clientName,
      });
    }

    if (subClientsData?.items) {
      subClientsData.items.forEach((subClient) => {
        const subClientName =
          subClient.companyName ||
          [subClient.firstName, subClient.lastName]
            .filter(Boolean)
            .join(" ")
            .trim() ||
          "Unnamed Subclient";
        options.push({
          value: subClient.id,
          label: `${subClientName} (Subclient)`,
        });
      });
    }

    return options;
  }, [client, subClientsData]);

  // Set default client to current client
  useEffect(() => {
    if (client && !form.getFieldValue("clientId")) {
      form.setFieldsValue({ clientId: client.id });
    }
  }, [client, form]);

  // Set default status to NO_POSSESSION
  useEffect(() => {
    form.setFieldsValue({ status: InventoryStatusCode.NO_POSSESSION });
  }, [form]);

  // Auto-fill dimensions when container type is selected for packages
  useEffect(() => {
    if (containerType && itemType === InventoryType.PACKAGE) {
      const selectedContainer = containerTypes.find(
        (item) => item.id === containerType
      );

      if (selectedContainer && selectedContainer.dimensions) {
        const currentLength = form.getFieldValue("length");
        const currentWidth = form.getFieldValue("width");
        const currentHeight = form.getFieldValue("height");

        if (!currentLength && !currentWidth && !currentHeight) {
          form.setFieldsValue({
            length: selectedContainer.dimensions.length,
            width: selectedContainer.dimensions.width,
            height: selectedContainer.dimensions.height,
            packingType:
              containerTypeToPackingType[containerType as ContainerType],
          });
        } else {
          form.setFieldsValue({
            packingType:
              containerTypeToPackingType[containerType as ContainerType],
          });
        }
      }
    }
  }, [containerType, form, itemType]);

  const queryClient = useQueryClient();
  const getPresignedMutation = useGetPresignedUrls();
  const uploadPresignedMutation = useUploadFileToPresignedUrl();
  const createImagesMutation = useCreateImages();
  const { mutate: createInventory } = useCreateMyInventory();
  const { success, error } = useNotification();

  const beforeUpload: UploadProps["beforeUpload"] = (file) => {
    setUploading(true);
    try {
      const previewUrl = URL.createObjectURL(file);
      setPreviewImages((prev) => [...prev, { url: previewUrl, file }]);
    } catch (err) {
      console.error("Error creating file preview:", err);
    } finally {
      setUploading(false);
    }
    return false;
  };

  const handleRemoveFile = (index: number) => {
    const urlToRevoke = previewImages[index].url;
    const newPreviews = previewImages.filter((_, i) => i !== index);
    setPreviewImages(newPreviews);
    URL.revokeObjectURL(urlToRevoke);
  };

  const handleSubmit = async (values: CreateInventoryFormData) => {
    setIsLoading(true);

    let uploadedImageIds: string[] = [];
    const files = previewImages.map((preview) => preview.file);

    if (files.length > 0) {
      try {
        const fileParams = files.map((file) => ({
          fileName: file.name,
          mimeType: file.type,
        }));

        const presignedUrls = await getPresignedMutation.mutateAsync(
          fileParams
        );

        const uploadedUrls: TPresignedUrl[] = [];
        for (const [index, urlData] of presignedUrls.entries()) {
          const file = files[index];
          if (file) {
            await uploadPresignedMutation.mutateAsync({
              presignedUrl: urlData.presignedUrl,
              file,
            });
            uploadedUrls.push(urlData);
          }
        }

        if (uploadedUrls.length > 0) {
          const images = uploadedUrls.map((presigned, index) => {
            const file = files[index];
            return {
              url: presigned.url,
              name: presigned.fileName,
              size: file.size?.toString(),
            };
          });

          const createdImages = await createImagesMutation.mutateAsync({
            images,
          });
          if (Array.isArray(createdImages)) {
            uploadedImageIds = createdImages.map((img: any) => img.id);
          } else if (
            createdImages &&
            typeof createdImages === "object" &&
            "id" in createdImages
          ) {
            uploadedImageIds = [createdImages.id];
          }
        }
      } catch (uploadError) {
        console.error("Error uploading images:", uploadError);
        error({
          message: "Error uploading images",
          description: "There was an error uploading the images.",
        });
        setIsLoading(false);
        return;
      }
    }

    const finalValues = {
      ...values,
      medium:
        values.medium === "Other" && values.customMedium
          ? values.customMedium
          : values.medium,
      status: InventoryStatusCode.NO_POSSESSION, // Force NO_POSSESSION
      imageIds: uploadedImageIds,
    };
    delete finalValues.customMedium;

    createInventory(finalValues, {
      onSuccess: () => {
        form.resetFields();
        setPreviewImages([]);
        // Invalidate all inventory queries to refresh the list
        queryClient.invalidateQueries({
          queryKey: [INVENTORY_KEYS.INVENTORIES],
        });
        success({
          message: "Inventory item created successfully!",
          description: "The inventory item has been added.",
        });
        onSuccess?.();
      },
      onError: (err) => {
        console.error("Error creating inventory:", err);
        error({
          message: "Error creating inventory item",
          description: "There was an error creating the inventory item.",
        });
      },
      onSettled: () => setIsLoading(false),
    });
  };

  return (
    <div className="bg-[#fffff5] p-6">
      <Form
        layout="vertical"
        size="large"
        form={form}
        onFinish={handleSubmit}
        className="space-y-4"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Form.Item
            label={
              <span className="text-xs font-bold uppercase tracking-wide text-black">
                Item Type
              </span>
            }
            name="itemType"
            rules={[{ required: true, message: "Please select an item type" }]}
          >
            <Select
              placeholder="Select item type"
              className="border-2 border-black"
              options={[
                { label: "Piece", value: InventoryType.PIECE },
                { label: "Package", value: InventoryType.PACKAGE },
              ]}
            />
          </Form.Item>

          <Form.Item
            label={
              <span className="text-xs font-bold uppercase tracking-wide text-black">
                Client
              </span>
            }
            name="clientId"
            rules={[{ required: true, message: "Please select a client" }]}
          >
            <Select
              placeholder="Select a client"
              className="border-2 border-black"
              options={clientOptions}
            />
          </Form.Item>

          {itemType === InventoryType.PACKAGE && (
            <Form.Item
              label={
                <span className="text-xs font-bold uppercase tracking-wide text-black">
                  Container Type
                </span>
              }
              name="containerType"
              rules={[
                { required: true, message: "Please select a container type" },
              ]}
            >
              <Select
                options={containerTypes.map((type) => ({
                  label: type.name,
                  value: type.id,
                }))}
                placeholder="Select a container type"
                className="border-2 border-black"
              />
            </Form.Item>
          )}

          {itemType === InventoryType.PIECE && (
            <Form.Item
              label={
                <span className="text-xs font-bold uppercase tracking-wide text-black">
                  Medium
                </span>
              }
              name="medium"
              rules={[{ required: true, message: "Please select a medium" }]}
            >
              <Select
                options={mediumTypes.map((type) => ({
                  label: type,
                  value: type,
                }))}
                placeholder="Select a medium"
                className="border-2 border-black"
              />
            </Form.Item>
          )}

          {itemType === InventoryType.PIECE && medium === "Other" && (
            <Form.Item
              label={
                <span className="text-xs font-bold uppercase tracking-wide text-black">
                  Custom Medium
                </span>
              }
              name="customMedium"
              rules={[
                { required: true, message: "Please enter a custom medium" },
              ]}
            >
              <Input
                placeholder="Enter custom medium type"
                className="border-2 border-black"
              />
            </Form.Item>
          )}

          <div className="md:col-span-2">
            <Form.Item
              label={
                <span className="text-xs font-bold uppercase tracking-wide text-black">
                  Title/Description
                </span>
              }
              name="title"
              rules={[
                {
                  required: true,
                  message: "Please enter a title or description",
                },
              ]}
            >
              <Input
                placeholder="Enter title or description"
                className="border-2 border-black"
              />
            </Form.Item>
          </div>

          <div className="md:col-span-2">
            <Form.Item
              label={
                <span className="text-xs font-bold uppercase tracking-wide text-black">
                  Artist
                </span>
              }
              name="artistName"
            >
              <Input
                placeholder="Enter artist name (optional)"
                className="border-2 border-black"
              />
            </Form.Item>
          </div>

          <div className="md:col-span-2">
            <Form.Item
              label={
                <span className="text-xs font-bold uppercase tracking-wide text-black">
                  Client Reference #
                </span>
              }
              name="clientInternalRef"
            >
              <Input
                placeholder="Enter client reference number"
                className="border-2 border-black"
              />
            </Form.Item>
          </div>

          <div className="md:col-span-2">
            <div className="grid grid-cols-3 gap-4">
              <Form.Item
                label={
                  <span className="text-xs font-bold uppercase tracking-wide text-black">
                    Length (inches)
                  </span>
                }
                name="length"
                rules={[{ required: true, message: "Please enter length" }]}
              >
                <InputNumber
                  className="w-full border-2 border-black"
                  min="0"
                  step="0.25"
                  placeholder="0.00"
                />
              </Form.Item>
              <Form.Item
                label={
                  <span className="text-xs font-bold uppercase tracking-wide text-black">
                    Depth (inches)
                  </span>
                }
                name="width"
                rules={[{ required: true, message: "Please enter width" }]}
              >
                <InputNumber
                  className="w-full border-2 border-black"
                  min="0"
                  step="0.25"
                  placeholder="0.00"
                />
              </Form.Item>
              <Form.Item
                label={
                  <span className="text-xs font-bold uppercase tracking-wide text-black">
                    Height (inches)
                  </span>
                }
                name="height"
                rules={[{ required: true, message: "Please enter height" }]}
              >
                <InputNumber
                  className="w-full border-2 border-black"
                  min="0"
                  step="0.25"
                  placeholder="0.00"
                />
              </Form.Item>
            </div>
          </div>

          <Form.Item
            label={
              <span className="text-xs font-bold uppercase tracking-wide text-black">
                Weight (lbs)
              </span>
            }
            name="weight"
            rules={[{ required: true, message: "Please enter weight" }]}
          >
            <InputNumber
              className="w-full border-2 border-black"
              min="0"
              step="0.25"
              placeholder="0.00"
            />
          </Form.Item>

          {itemType === InventoryType.PIECE && (
            <Form.Item
              label={
                <span className="text-xs font-bold uppercase tracking-wide text-black">
                  Value ($)
                </span>
              }
              name="value"
            >
              <InputNumber
                className="w-full border-2 border-black"
                min="0"
                step="0.01"
                placeholder="0.00"
              />
            </Form.Item>
          )}

          {itemType === InventoryType.PIECE && (
            <Form.Item
              label={
                <span className="text-xs font-bold uppercase tracking-wide text-black">
                  Packing Type
                </span>
              }
              name="packingType"
              rules={[
                { required: true, message: "Please select a packing type" },
              ]}
            >
              <Select
                options={Object.values(PackingTypes).map((type) => ({
                  label: capitalize(type),
                  value: type,
                }))}
                placeholder="Select a packing type"
                className="border-2 border-black"
              />
            </Form.Item>
          )}

          <div className="md:col-span-2">
            <Form.Item
              label={
                <span className="text-xs font-bold uppercase tracking-wide text-black">
                  Photos/Attachments
                </span>
              }
            >
              <Upload
                accept=".jpg,.jpeg,.png,.gif"
                beforeUpload={beforeUpload}
                showUploadList={false}
                multiple={true}
                disabled={uploading}
              >
                <Button
                  htmlType="button"
                  icon={<UploadOutlined />}
                  loading={uploading}
                  disabled={uploading}
                  className="border-2 border-black bg-[#fffff5] text-black font-bold text-xs uppercase tracking-wide hover:bg-[#fffef5]"
                >
                  {uploading ? "Uploading..." : "Click to Upload"}
                </Button>
              </Upload>

              {previewImages.length > 0 && (
                <div className="mt-3 grid grid-cols-4 gap-4">
                  {previewImages.map((preview, index) => (
                    <div
                      key={`preview-${index}`}
                      className="relative border-2 border-black rounded-lg p-1"
                    >
                      <div className="w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                        <Image
                          src={preview.url}
                          className="object-contain max-w-full max-h-full"
                          alt={preview.file.name}
                          rootClassName="w-full h-full"
                        />
                      </div>
                      <button
                        type="button"
                        className="absolute top-1 right-1 bg-white rounded-full p-1 shadow-sm hover:bg-gray-100 border-2 border-black"
                        onClick={() => handleRemoveFile(index)}
                      >
                        <X size={14} />
                      </button>
                      <div className="text-xs text-gray-500 truncate mt-1 px-1">
                        {preview.file.name}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Form.Item>
          </div>

          <div className="md:col-span-2">
            <Form.Item
              label={
                <span className="text-xs font-bold uppercase tracking-wide text-black">
                  Notes
                </span>
              }
              name="notes"
            >
              <Input.TextArea
                rows={4}
                placeholder="Enter any additional notes or information..."
                className="border-2 border-black"
              />
            </Form.Item>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t-2 border-black">
          {onCancel && (
            <Button
              htmlType="button"
              onClick={onCancel}
              className="px-4 py-2 border-2 border-black bg-[#fffff5] text-black font-bold text-xs uppercase tracking-wide hover:bg-[#fffef5]"
            >
              Cancel
            </Button>
          )}
          <Button
            loading={isLoading}
            htmlType="submit"
            className="px-4 py-2 border-2 border-black bg-black text-white font-bold text-xs uppercase tracking-wide hover:opacity-90 flex items-center gap-2"
          >
            <Package size={16} />
            Create Inventory
          </Button>
        </div>
      </Form>
    </div>
  );
};

